import {
  useEffect,
  useMemo, useRef, useState
} from 'react';

import {
  FormField, REGEX_BUSINESS_EMAIL, REGEX_EMAIL, validateBusinessEmail, validateEmail
} from '@rippling/utils';
import clsx from 'clsx';

import { FormFieldType, SurfaceLevel } from '../../types';
import Button, { ButtonProps } from './Button';
import InputLabel from './InputLabel';

export interface TextInputProps {
  label: string;
  name: string;
  type: Exclude<FormFieldType, FormFieldType.Select>;
  buttonProps?: ButtonProps;
  disabled?: boolean;
  hasHeading?: boolean;
  isBusinessEmail?: boolean;
  isLabelOutside?: boolean;
  isRequired?: boolean;
  onChange?: (name: string, value: string) => void;
  onInvalid?: () => void;
  pattern?: string;
  placeholder?: string;
  surfaceLevel?: SurfaceLevel;
  value?: string;
}

const TextInput = ({
  buttonProps,
  disabled = false,
  hasHeading = false,
  isBusinessEmail = false,
  isLabelOutside: originalIsLabelOutside = false,
  isRequired = false,
  label,
  name,
  onChange,
  onInvalid,
  pattern,
  placeholder,
  surfaceLevel = SurfaceLevel.L0,
  type,
  value,
}: TextInputProps) => {
  const [internalValue, setInternalValue] = useState<null | string>(value || null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLSpanElement>(null);
  const isTextArea = type === FormFieldType.TextArea;
  const isControlled = value !== undefined;
  const valueToUse = isControlled ? value : internalValue;

  const borderClassName = surfaceLevel === SurfaceLevel.L0
    ? 'border-border-l0 invalid:placeholder-shown:border-border-l0'
    : 'border-border-l1 invalid:placeholder-shown:border-border-l1';
  const backgroundClassName = surfaceLevel === SurfaceLevel.L0 ? 'bg-surface-l1' : 'bg-surface-l2';
  const Component = isTextArea ? 'textarea' : 'input';
  const id = name;
  const isLabelOutside = buttonProps ? false : originalIsLabelOutside;

  // We need to redo this. HTML input "pattern" attributes uses the "v" mode
  // https://v8.dev/features/regexp-v-flag
  // This generates error in console -- Invalid character in character class
  const patternToUse = useMemo(() => {
    // Use pattern if provided
    if (pattern) {
      return pattern;
    }

    if (type === FormFieldType.Email) {
      const regex = isBusinessEmail ? REGEX_BUSINESS_EMAIL : REGEX_EMAIL;

      return regex.source;
    }
  }, []);

  const useMinNumber = useMemo(() => {
    // Set min for user employees
    if (name === FormField.NumberOfPeople) {
      return 0;
    }

    return null;
  }, [name]);

  useEffect(() => {
    const inputEl = inputContainerRef.current?.querySelector('input');

    if (!inputEl || !buttonRef.current) {
      return;
    }

    inputEl.style.paddingRight = `${(buttonRef.current.getBoundingClientRect().width ?? 0) + 16}px`;
  }, []);

  const inputLabelTextAreaClass = () => {
    if (isTextArea) {
      if (valueToUse?.length > 0) {
        return 'top-2';
      } else {
        return 'top-8';
      }
    } else {
      if (valueToUse?.length > 0) {
        return 'top-2';
      } else {
        return 'top-1/2';
      }
    }
  };

  return (
    <div
      className={clsx(
        'flex gap-2xs',
        isLabelOutside ? 'flex-col' : 'flex-row',
        isLabelOutside && !hasHeading && 'pt-xs',
        !!buttonProps && 'relative'
      )}
    >
      {isLabelOutside && (
        <InputLabel
          className="!text-text-secondary"
          id={id}
          isRequired={isRequired}
          label={label}
        />
      )}
      <div ref={inputContainerRef} className="relative w-full">
        <Component
          ref={(el: HTMLInputElement | HTMLTextAreaElement | null) => {
            let customMessage = '';

            if (valueToUse && patternToUse && id === FormField.Email) {
              if (isBusinessEmail && !validateBusinessEmail(valueToUse)) {
                customMessage = 'Please use your business email';
              } else if (!validateEmail(valueToUse)) {
                customMessage = 'Invalid email';
              }
            }

            el?.setCustomValidity(customMessage);
          }}
          className={clsx(
            'peer w-full rounded-sm border',
            borderClassName,
            isLabelOutside || isTextArea ? 'p-sm' : 'pt-lg pb-2xs px-sm',
            disabled ? 'bg-plum-800' : backgroundClassName,
            disabled && 'cursor-not-allowed',
            !disabled && 'outline-none can-hover:hover:!border-border-active focus:!border-border-active',
            'placeholder:text-text-primary-inactive text-text-primary',
            valueToUse !== null && 'invalid:border-border-error'
          )}
          disabled={disabled}
          id={name}
          min={useMinNumber ?? undefined}
          onChange={(e) => {
            const newValue = e.target.value;

            setInternalValue(newValue);
            onChange?.(name, newValue);
          }}
          onInvalid={onInvalid}
          pattern={patternToUse}
          placeholder={isTextArea ? placeholder : ''}
          required={isRequired}
          value={valueToUse ?? ''}
          {...(isTextArea ? {
            cols: 50,
            rows: 3,
          } : { type: type })}
        />
        {(!isLabelOutside || !!placeholder && valueToUse === null) && !isTextArea && (
          <InputLabel
            className={clsx(
              'transition-input absolute left-4  pointer-events-none',
              !isLabelOutside && 'peer-focus:top-2 peer-focus:translate-y-0 peer-focus:text-caption',
              inputLabelTextAreaClass(),
              valueToUse?.length > 0 ? 'text-caption translate-y-0 ' : 'text-p2 -translate-y-1/2'
            )}
            id={id}
            isRequired={isRequired}
            label={isLabelOutside ? placeholder ?? '' : label}
          />
        )}
      </div>
      {!!buttonProps && (
        <span ref={buttonRef} className="absolute top-1 bottom-1 right-1 w-fit hidden sm:block">
          <Button {...buttonProps} className="h-full flex items-center" />
        </span>
      )}
    </div>
  );
};

export default TextInput;
